import React, { useRef, useState } from "react";
import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerOverlay,
  Flex,
  Input,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { AppBar, AppIcon, APP } from "../../components/mobileUi";
import {
  DesktopBack,
  DesktopPage,
  DesktopPanel,
  useIsDesktop,
} from "../../components/desktopUi";
import { appImages } from "../../assets/appicons";
import { createTransferRequestApi, searchSalesEmployeeApi } from "../../api/finance.common.api";
import { extractErrorMessage } from "../../api/client";
import { EMP_BASE_PATH } from "../../config";

const BORDER = "#DCE1EA";
const TEXT = "#08142D";
const MUTED = "#94A3B8";
const GRAD = "linear(to-r, #68A2FF, #2168DA)";

const CFG = {
  booking: { type: "BOOKING", idKey: "bookingId", title: "Chuyển booking", back: "/tai-chinh/booking" },
  locking: { type: "LOCKING", idKey: "lockingId", title: "Chuyển locking", back: "/tai-chinh/locking" },
  ttkq: { type: "TTKQ", idKey: "ttkqId", title: "Chuyển TTKQ", back: "/tai-chinh/ttkq" },
  salesContract: { type: "SALESCONTRACT", idKey: "salesContractId", title: "Chuyển HDMB", back: "/tai-chinh/hop-dong-mua-ban" },
};

const TransferRequest = ({ kind = "booking" }) => {
  const isDesktop = useIsDesktop();
  const cfg = CFG[kind] || CFG.booking;
  const navigate = useNavigate();
  const { id } = useParams();
  const [employee, setEmployee] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  const sheet = useDisclosure();
  const [emps, setEmps] = useState([]);
  const [empSearch, setEmpSearch] = useState("");
  const timer = useRef(null);

  const loadEmps = (kw) =>
    searchSalesEmployeeApi({ page: 1, limit: 30, search: kw || undefined })
      .then((d) => setEmps(d?.items || d || []))
      .catch(() => setEmps([]));
  const openPicker = () => { setEmpSearch(""); loadEmps(""); sheet.onOpen(); };
  const onEmpSearch = (e) => {
    const v = e.target.value;
    setEmpSearch(v);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => loadEmps(v.trim()), 300);
  };

  const submit = async () => {
    if (!employee) { setErr("Vui lòng chọn nhân viên nhận"); toast.error("Chưa chọn nhân viên muốn chuyển"); return; }
    setSubmitting(true);
    try {
      await createTransferRequestApi({ newEmployeeId: employee._id, [cfg.idKey]: id, type: cfg.type });
      toast.success(`${cfg.title} thành công`);
      navigate(`${EMP_BASE_PATH}${cfg.back}`);
    } catch (e) {
      toast.error(extractErrorMessage(e, `${cfg.title} thất bại`));
    } finally {
      setSubmitting(false);
    }
  };

  const pageBody = (
    <>

      <Box px="16px" pt="16px" flex="1" overflowY="auto">
        <Box mb="16px">
          <Text fontSize="13px" fontWeight={600} mb="6px" color={TEXT}>Nhân viên nhận <Text as="span" color="#EF2E14">*</Text></Text>
          <Flex as="button" type="button" onClick={openPicker} align="center" justify="space-between" h="46px" px="12px" border="1px solid" borderColor={err ? "#EF2E14" : BORDER} borderRadius="8px" w="100%">
            <Text fontSize="14px" color={employee ? TEXT : MUTED} noOfLines={1}>{employee ? `${employee.fullName}${employee.code ? " - " + employee.code : ""}` : "Chọn nhân viên"}</Text>
            <img src={appImages.icon_chevron_down} alt="" width={18} height={18} style={{ objectFit: "contain" }} />
          </Flex>
          {err && <Text fontSize="12px" color="red.500" mt="4px">{err}</Text>}
        </Box>
      </Box>

      <Box borderTop="1px solid #EEF1F6" p="16px">
        <Flex gap="16px">
          <Button flex="1" h="50px" variant="outline" borderColor={APP.primary200} color={APP.primary200} borderRadius="8px" _hover={{ bg: "#EAF2FF" }} onClick={() => navigate(-1)}>Hủy</Button>
          <Button flex="1" h="50px" bgImage={GRAD} color="white" borderRadius="8px" fontWeight={700} _hover={{ bgImage: GRAD, opacity: 0.92 }} isLoading={submitting} onClick={submit}>Lưu</Button>
        </Flex>
      </Box>

      <Drawer isOpen={sheet.isOpen} onClose={sheet.onClose} placement="bottom">
        <DrawerOverlay bg="rgba(0,0,0,0.5)" />
        <DrawerContent borderTopRadius="16px" maxW="480px" mx="auto">
          <DrawerBody py="16px" px="16px">
            <Text fontSize="16px" fontWeight={700} color={TEXT} mb="8px">Chọn nhân viên</Text>
            <Flex h="40px" align="center" px="10px" bg="#F7F9FC" borderRadius="8px" mb="8px">
              <AppIcon src={appImages.icon_search} size={18} color={MUTED} />
              <Input variant="unstyled" ml="8px" fontSize="14px" placeholder="Tìm nhân viên..." value={empSearch} onChange={onEmpSearch} />
            </Flex>
            <Box maxH="50vh" overflowY="auto">
              {emps.map((c, i) => (
                <Flex key={c._id} as="button" w="100%" align="center" justify="space-between" py="12px" borderBottom={i === emps.length - 1 ? "none" : "1px solid #EEF1F6"} onClick={() => { setEmployee(c); setErr(""); sheet.onClose(); }}>
                  <Box textAlign="left">
                    <Text fontSize="15px" color={employee?._id === c._id ? APP.primary200 : TEXT}>{c.fullName}</Text>
                    <Text fontSize="12px" color={MUTED}>{c.code || ""}</Text>
                  </Box>
                  {employee?._id === c._id && <img src={appImages.icon_check} alt="" width={18} height={18} style={{ objectFit: "contain" }} />}
                </Flex>
              ))}
              {emps.length === 0 && <Text fontSize="13px" color={MUTED} textAlign="center" py="16px">Không có nhân viên</Text>}
            </Box>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );

  // ===================== Bản WEB (desktop) =====================
  if (isDesktop) {
    return (
      <DesktopPage title={cfg.title} right={<DesktopBack onClick={() => navigate(-1)} />}>
        <DesktopPanel p="0" pb="24px">
          {pageBody}
        </DesktopPanel>
      </DesktopPage>
    );
  }

  return (
    <Box maxW="480px" mx="auto" bg="white" minH="100dvh" display="flex" flexDirection="column">
      <AppBar title={cfg.title} onBack={() => navigate(-1)} />
      {pageBody}
    </Box>
  );
};

export default TransferRequest;
